import React, { useState, useEffect } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { EnumType } from 'json-to-graphql-query'
import _ from 'lodash'

import NotificationList from './components/NotificationList'
import NotificationSettings from './components/NotificationSettings'
import NotificationSettingsFooter from './components/NotificationSettingsFooter'
import SlidePanel from 'components/SlidePanel'
import NotificationUnreadToggle from './components/NotificationUnreadToggle'
import { VuiButtonInput, VuiFilterChip, VuiCustomScroll } from '@topkamera/viso-ui'
import { getNotificationState } from 'containers/App/selectors'
import { updateNotificationDisplay } from 'containers/App/actions'
import { markAsReadNotification } from 'containers/Notification/actions';
import { NOTIFICATION_VIEW, SETTING_VIEW } from './constants'
import { updateNotificationPermissions, getNotifications } from 'api/notifications'
import { defaultNotifier } from 'functions/notificationHandler'
import { handleMutation, handleApiResponse } from 'utils/request'
import './Notification.scss'

const Notification = ({
  showNotification, updateNotificationDisplay, markAsReadNotification, color = 'accent0', router, workspace
}) => {
  const [activeState, setActiveState] = useState(NOTIFICATION_VIEW)
  const [values, setValues] = useState([])
  const [untouchedValues, setUntouchedValues] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [isTouched, toggleTouched] = useState(false)
  const [unreadOnly, toggleUnreadOnly] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState([])
  const [searchFilter, setSearchFilter] = useState('')
  const [radioList, setRadioList] = useState([])
  const [categoryFilterHeight, setCategoryFilterHeight] = useState(undefined)
  const [notificationList, setNotificationList] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [notificationCount, setNotificationCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const pageSize = 30

  const onTabChange = (state = NOTIFICATION_VIEW) => {
    setActiveState(state)
  }

  const closeNotification = () => {
    updateNotificationDisplay(false)
    onTabChange()
  }

  const onSave = async () => {
    setIsSaving(true)
    const onSuccess = () => {
      onTabChange()
      defaultNotifier('Notification settings saved successfully')
    }
    try {
      const response = await updateNotificationPermissions({ permissions: values })
      handleMutation(response, onSuccess)
    } catch (error) {
      defaultNotifier(error)
    } finally {
      setIsSaving(false)
    }
  }

  const onFilter = (value, type) => {
    const resetParams = {
      page: 1,
      onSuccess: () => setCurrentPage(1)
    }
    if (type === 'search') {
      setSearchFilter(value)
      onLoadData({ ...resetParams, search: value })
    }
    if (type === 'category') {
      const newValue = value?.[0]?.value === 'all' ? [] : value
      setCategoryFilter(newValue)
      onLoadData({ ...resetParams, category: newValue })
    }
    if (type === 'readState') {
      toggleUnreadOnly(value)
      setNotificationList([])
      onLoadData({ ...resetParams, readState: value })
    }
  }

  const onLoadMore = async () => {
    const nextPage = currentPage + 1
    onLoadData({
      onSuccess: () => {
        setCurrentPage(nextPage)
      },
      page: nextPage
    })
  }

  const onLoadData = async ({
    readState = unreadOnly,
    category = categoryFilter,
    search = searchFilter,
    page = currentPage,
    onSuccess = () => { }
  } = {}) => {
    if (isLoading) return
    const updateList = (response) => {
      const updatedList = [...(page > 1 ? notificationList : []), ...response.notificationList]
      const getCategories = _.uniqBy(updatedList, 'category')
      const categoryCount = getCategories?.length || 0
      setNotificationList(updatedList)
      setNotificationCount(response.count)
      setRadioList(getCategories.map((item) => ({ label: item.category, value: item.category })))
      setCategoryFilterHeight(categoryCount > 6 ? undefined : categoryCount * 30 + 22)
      onSuccess?.(updatedList)
    }
    setIsLoading(true)
    try {
      const response = await getNotifications({
        offset: (page - 1) * pageSize, limit: pageSize,
        sort: {
          field: 'createdAt',
          type: new EnumType('DESC'),
        },
        query: {
          single: [
            ...(readState ? [{ name: 'readState', operator: new EnumType('equalTo'), value: false }] : []),
            ...(category[0]?.value ? [{ name: 'category', operator: new EnumType('equalTo'), value: category[0]?.value }] : []),
            ...(search ? [{ name: 'message', operator: new EnumType('contains'), value: search }] : []),
          ],
        },
      })
      handleApiResponse(response, updateList, 'notifications')
    } catch (error) {
      console.error('Error loading notifications:', error)
      defaultNotifier(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    onLoadData()
  }, [])

  useEffect(() => {
    updateNotificationDisplay(false)
  }, [updateNotificationDisplay])

  return (
    <>
      <SlidePanel
        isVisible={showNotification}
        hasFadeOutScroll
        color={color}
        noScroll
        contentClassName='viso-notification-panel'
        onClose={closeNotification}
        customFooter={activeState === SETTING_VIEW &&
          <NotificationSettingsFooter
            onSave={onSave}
            onCancel={() => {
              setValues(untouchedValues)
              toggleTouched(false)
            }}
            isTouched={isTouched}
            isSaving={isSaving}
          />
        }
        headerProps={{
          title: 'Notification Center',
          customAction: (
            activeState === NOTIFICATION_VIEW &&
            <NotificationUnreadToggle
              unreadOnly={unreadOnly}
              toggleUnreadOnly={() => onFilter(!unreadOnly, 'readState')}
            />
          ),
          tabs: [
            {
              label: 'Notifications',
              onClick: () => onTabChange(NOTIFICATION_VIEW),
              active: activeState === NOTIFICATION_VIEW
            },
            {
              label: 'Settings',
              onClick: () => onTabChange(SETTING_VIEW),
              active: activeState === SETTING_VIEW
            }
          ]
        }}
      >
        <VuiCustomScroll
          fadeBottom
          contentClassName='viso-notification-panel__scroll'
          lazyLoad={{
            onLazyLoad: activeState === NOTIFICATION_VIEW && onLoadMore,
            totalCount: notificationCount,
            pageSize: pageSize,
            currentPage: currentPage
          }}
        >
          {activeState === NOTIFICATION_VIEW &&
            <>
              <div className='vui-flex vui-gap-8 vui-pb-20'>
                <VuiFilterChip
                  label='Category'
                  options={radioList}
                  type='radioinput'
                  hasAll
                  value={categoryFilter}
                  scrollHeight={categoryFilterHeight}
                  onApply={(value) => onFilter(value, 'category')}
                  elementId='slidePanelPopover'
                />
                <VuiButtonInput
                  id='notificationListSearch'
                  iconLeft='search'
                  onChange={(value) => onFilter(value, 'search')}
                  color={color}
                />
              </div>
              <NotificationList
                onClose={closeNotification}
                list={notificationList}
                router={router}
                workspace={workspace}
                markAsReadNotification={markAsReadNotification}
                setNotificationList={setNotificationList}
                unreadOnly={unreadOnly}
              />
            </>
          }
          {activeState === SETTING_VIEW &&
            <NotificationSettings
              setValues={setValues}
              setUntouchedValues={setUntouchedValues}
              values={values}
              toggleTouched={toggleTouched}
            />
          }
        </VuiCustomScroll>
      </SlidePanel>
    </>
  )
}

const mapStateToProps = createStructuredSelector({
  showNotification: getNotificationState()
})

const withConnect = connect(mapStateToProps, { updateNotificationDisplay, markAsReadNotification })
export default compose(withConnect)(Notification)